// Hover help: an experiment's look (colours, contrast mode), its edge jitter and timing.

import { DEFAULT_APPEARANCE, DEFAULT_EDGE_JITTER, DEFAULT_TIMING, JITTER_AXES, type EdgeJitter, type RGB } from "../lib/protocol";
import { ContrastModePicture, JitterPicture, TimingPicture } from "./pictures";
import type { HelpContext, HelpEntry } from "./types";

const m = (body: string) => `<math display="block">${body}</math>`;
const mi = (s: string) => `<mi>${s}</mi>`;
const mn = (s: string | number) => `<mn>${s}</mn>`;
const mo = (s: string) => `<mo>${s}</mo>`;
const sub = (a: string, b: string) => `<msub>${a}<mtext>${b}</mtext></msub>`;
const frac = (a: string, b: string) => `<mfrac><mrow>${a}</mrow><mrow>${b}</mrow></mfrac>`;

const css = (rgb: RGB) => `rgb(${rgb.map((v) => Math.round(255 * Math.min(1, Math.max(0, v)) ** (1 / 2.2))).join(",")})`;

function jitterOf(ctx: HelpContext): EdgeJitter {
  return { ...DEFAULT_EDGE_JITTER, ...(ctx.jitter ?? ctx.acuity?.edge_jitter ?? ctx.contrast?.edge_jitter ?? {}) };
}

function jitterPic(ctx: HelpContext) {
  const j = jitterOf(ctx);
  const [c1, c2] = j.colour_source === "preset" ? [j.color1_rgb_rel, j.color2_rgb_rel] : JITTER_AXES["+L-M / -L+M"]!;
  return <JitterPicture inner={j.unit === "stroke" ? j.inner_width : j.inner_width / 25} outer={j.unit === "stroke" ? j.outer_width : j.outer_width / 25}
    blob={j.unit === "stroke" ? j.blob_size : j.blob_size / 25} density={j.density} intensity={j.intensity}
    every={j.update_frames} colours={[css(c1), css(c2)]} />;
}

function lookOf(ctx: HelpContext) {
  const e = ctx.experiment;
  const a = e?.appearance ?? DEFAULT_APPEARANCE;
  const bgRel = e?.background_rel ?? 1;
  return { mode: a.contrast_mode, bg: (a.background_rgb_rel ?? [bgRel, bgRel, bgRel]) as RGB, letter: a.letter_rgb_rel };
}

export const LOOK_HELP: Record<string, HelpEntry> = {
  "look.colours": {
    title: "Colours",
    what: "The background and the letter, each as red, green and blue in rel (the share of each primary's full output, "
      + "0–1, linear in light, not the 0–255 of a picture file). Black on white is letter (0, 0, 0) on (1, 1, 1). Coloured "
      + "letters test colour vision as well as acuity or contrast; results are kept as a separate series for each look.",
    picture: (ctx) => { const l = lookOf(ctx); return <ContrastModePicture mode="toward_letter" bg={l.bg} letter={l.letter} />; },
    scenarios: [
      { scenario: "Standard", value: "black on white", why: "comparable with charts" },
      { scenario: "Blue-yellow (S-cone) emphasis", value: "yellow (1, 1, 0) on blue (0, 0, 1)", why: "as in the colour programs" },
    ],
  },
  "look.contrast_mode": {
    title: "How contrast letters get fainter",
    what: "Darker shade of the background (default): the letter is the background with less light, so contrast is Weber "
      + "luminance contrast and the score is log CS, as on the Pelli-Robson chart.\n\nFade toward the letter colour: the "
      + "letter moves from the background colour toward its own colour; the score is log modulation (not log CS), labelled "
      + "as such, and only comparable with itself.",
    math: m(`${mi("W")}${mo("=")}${frac(`${sub(mi("L"), "bg")}${mo("−")}${sub(mi("L"), "letter")}`, sub(mi("L"), "bg"))}${mo(",")}${mo("&#xA0;")}`
      + `${mi("log CS")}${mo("=")}${mo("−")}<msub>${mi("log")}${mn(10)}</msub>${mi("W")}`),
    mathNote: "W = 1 (black on white) is log CS 0; W = 0.01 is log CS 2.",
    picture: (ctx) => { const l = lookOf(ctx); return <ContrastModePicture mode={l.mode} bg={l.bg} letter={l.letter} />; },
  },
  "jitter.enabled": {
    title: "Edge jitter",
    what: "Small colour blobs, re-drawn every frame or few frames, in a band along the E's edges (visualTools' chromatic "
      + "outline). The blobs flicker between two colours that differ for the cones but are balanced in the band, so the "
      + "letter's outline is masked by colour noise; it tests how well the patient sees the letter through it.",
    picture: jitterPic,
  },
  "jitter.colours": {
    title: "Jitter colours",
    what: "The two colours the blobs take. Presets name the cone axis they target (+L−M / −L+M red-green; S-cone blue-yellow; "
      + "achromatic). Cone-isolating L vs M computes, for the session's own display, the pair that changes only the L and M "
      + "cones (S unchanged), from the display's primaries and the observer below.",
    picture: jitterPic,
  },
  "jitter.unit": {
    title: "Sizes in strokes or pixels",
    what: "Strokes: the band and blobs scale with the letter (0.2 stroke stays a fifth of a stroke at any size). Pixels: fixed "
      + "device pixels, as in visualTools.",
    picture: jitterPic,
  },
  "jitter.inner_width": { title: "Band inside the letter", what: "How far into the letter's strokes the blobs reach.", picture: jitterPic, range: "default 0.2 stroke." },
  "jitter.outer_width": { title: "Band outside the letter", what: "How far out from the letter's edges the blobs reach.", picture: jitterPic, range: "default 0.2 stroke." },
  "jitter.blob_size": { title: "Blob size", what: "The size of each square blob.", picture: jitterPic, range: "default 0.1 stroke." },
  "jitter.density": { title: "Density", what: "The share of blob positions coloured at each re-draw (1 = all).", picture: jitterPic, range: "0–1; default 1." },
  "jitter.update_frames": {
    title: "Re-draw every (frames)",
    what: "How often the blob pattern changes: 1 = every frame (60 times a second on a 60 Hz screen); larger values flicker slower.",
    math: m(`${mi("f")}${mo("=")}${frac(sub(mi("f"), "screen"), mi("n"))}`),
    picture: jitterPic,
    range: "1–120; default 1.",
  },
  "jitter.intensity": { title: "Intensity", what: "How strongly the blob colour replaces the pixel underneath (mix weight 0–1).", picture: jitterPic, range: "default 1." },
  "jitter.link_to_contrast": {
    title: "Strength with contrast",
    what: "Linked: the blobs fade with the letter's contrast, so a faint letter has faint blobs. Full strength: the blobs stay "
      + "at full intensity whatever the letter's contrast.",
    picture: jitterPic,
  },
  "jitter.cone_mode": {
    title: "Cone-isolating direction",
    what: "Cone contrast: the two colours give equal and opposite L and M cone contrasts (S unchanged). Isoluminant: the pair "
      + "is chosen so luminance does not change.",
  },
  "jitter.cone_center": {
    title: "Centred on",
    what: "The colour the pair is balanced around: mid-grey gives the most room on any display; the background keeps the "
      + "average equal to the background.",
  },
  "jitter.cone_observer": {
    title: "Whose cones",
    what: "The cone sensitivities used to compute the cone-isolating pair: the fundamentals, the peak wavelengths, the "
      + "lens (age) and macular pigment. The defaults are a standard observer of 32.",
  },
  "timing.stimulus_ms": {
    title: "Letter shown for",
    what: "How long each letter stays on screen. 0 (default): until the patient answers. A limit tests speed of seeing as "
      + "well and reduces scanning of the letter.",
    picture: (ctx) => { const t = (ctx.contrast ?? ctx.acuity)?.timing ?? DEFAULT_TIMING; return <TimingPicture stimulus={t.stimulus_ms} timeout={t.response_timeout_ms} isi={t.isi_ms} />; },
    range: "0 = until answered; ms.",
  },
  "timing.response_timeout_ms": {
    title: "Answer within",
    what: "After this long without an answer the trial counts as wrong (as \"can't see\"). 0 (default): no limit.",
    picture: (ctx) => { const t = (ctx.contrast ?? ctx.acuity)?.timing ?? DEFAULT_TIMING; return <TimingPicture stimulus={t.stimulus_ms} timeout={t.response_timeout_ms} isi={t.isi_ms} />; },
  },
  "timing.isi_ms": {
    title: "Gap between letters",
    what: "A blank screen between an answer and the next letter, so the next one is clearly new.",
    picture: (ctx) => { const t = (ctx.contrast ?? ctx.acuity)?.timing ?? DEFAULT_TIMING; return <TimingPicture stimulus={t.stimulus_ms} timeout={t.response_timeout_ms} isi={t.isi_ms} />; },
    range: "default 500 ms.",
  },
};
